/**
 * PostHog Analytics Configuration
 *
 * Following PostHog 2025 best practices for React apps
 * Documentation: https://posthog.com/docs/libraries/react
 */

import posthog from 'posthog-js'

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com'
const POSTHOG_ENABLED = import.meta.env.VITE_POSTHOG_ENABLED === 'true'
const ENV = import.meta.env.VITE_AUTH_AND_DB_ENV || 'development'

/**
 * Initialize PostHog
 * Called once at app startup
 */
export function initializePostHog() {
  // Only initialize if API key is provided and enabled
  if (!POSTHOG_KEY || !POSTHOG_ENABLED) {
    console.log('[PostHog] Analytics disabled - missing API key or VITE_POSTHOG_ENABLED=false')
    return false
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,

      // Person profiles (2025 best practice)
      person_profiles: 'identified_only', // Only create profiles for logged-in users

      // Privacy settings
      autocapture: false, // Disable autocapture for more control and privacy
      capture_pageview: false, // We'll manually track page views with TanStack Router
      capture_pageleave: true, // Track when users leave pages

      // Session recording (disabled by default)
      disable_session_recording: true, // Enable later if needed

      // Performance
      loaded: (posthog) => {
        if (ENV === 'development') {
          posthog.debug() // Enable debug mode in development
        }
      },

      // Cross-subdomain tracking
      cross_subdomain_cookie: false,

      // Respect DNT
      respect_dnt: true,

      // Advanced settings
      persistence: 'localStorage+cookie', // Best practice for SPA
      sanitize_properties: (properties) => {
        // Remove any sensitive data from properties
        const sanitized = { ...properties }
        delete sanitized.password
        delete sanitized.token
        delete sanitized.api_key
        return sanitized
      },
    })

    console.log('[PostHog] Analytics initialized successfully')
    return true
  } catch (error) {
    console.error('[PostHog] Initialization error:', error)
    return false
  }
}

/**
 * Check if PostHog is initialized and enabled
 */
export function isPostHogEnabled(): boolean {
  return POSTHOG_ENABLED && !!POSTHOG_KEY && posthog.__loaded
}

/**
 * Export posthog instance for direct access
 */
export { posthog }
